/**
 * Session recording with the MediaRecorder API.
 *
 * Two capture modes (`captureMode`):
 *   - 'screen': real tab/screen capture via getDisplayMedia (desktop browsers).
 *   - 'canvas': phones can't capture their screen, so TokDraw records the
 *     portrait recap canvas drawn by recap/compositor.js instead. It also keeps
 *     the screen awake, since a backgrounded page stops drawing.
 *
 * - `armCapture()` asks the browser for tab/screen capture (must run in a user
 *   gesture) or opens the canvas stream. It runs when the host switches *Record*
 *   on, before the show, so the browser's picker never interrupts it. The
 *   stream stays live until released.
 * - `startRecording()` … `stopRecording()` records ONE continuous file.
 *   main.js starts it when Present View opens and stops it after the final
 *   winner (or when Present View closes).
 * - MP4 (H.264/AAC) where the browser can record it, WebM otherwise. See
 *   lib/recording-format.js.
 * - Audio comes from TokDraw's own Web Audio graph (see fx/audio.js), so SFX
 *   are in the file even where the browser can't capture tab audio, and are
 *   never doubled.
 */
import { describeFormat, encoderFrameSize, pickRecordingFormat } from './lib/recording-format.js';

// Screen content at ≤1080p; keeps a 10-minute show under ~400 MB.
const VIDEO_BITS_PER_SECOND = 5_000_000;
// The recap canvas is 720×1280 with flat graphics; this keeps a phone's file small.
const CANVAS_VIDEO_BITS_PER_SECOND = 2_500_000;
const AUDIO_BITS_PER_SECOND = 128_000;
// Long edge of the captured video. TikTok re-encodes to 1080p anyway, and a
// cap keeps 4K screens from producing huge files. Works for portrait windows too.
const MAX_EDGE = 1920;

const CANVAS_FPS = 30;

const hasMediaRecorder = typeof window !== 'undefined' && typeof window.MediaRecorder === 'function';
const hasScreenCapture =
  typeof window !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia) && window.isSecureContext;
const hasCanvasCapture = typeof HTMLCanvasElement !== 'undefined' && typeof HTMLCanvasElement.prototype.captureStream === 'function';
// `?recording=canvas` forces the portrait recap video on a desktop browser too.
const wantsCanvas = typeof location !== 'undefined' && new URLSearchParams(location.search).get('recording') === 'canvas';

/** 'screen' | 'canvas' | null when this browser can't record at all. */
export const captureMode = !hasMediaRecorder
  ? null
  : hasScreenCapture && !(wantsCanvas && hasCanvasCapture)
    ? 'screen'
    : hasCanvasCapture
      ? 'canvas'
      : null;
export const recordingSupported = captureMode !== null;

/** Why recording is unavailable here, in words a host can act on; '' when it is supported. */
export function recordingUnsupportedReason() {
  if (recordingSupported) return '';
  if (typeof window === 'undefined') return '';
  return 'This browser can’t record video (MediaRecorder or canvas capture is missing). Try an up-to-date Chrome, Edge, Brave, Firefox or Safari.';
}

let displayStream = null;
// Screen mode: the encoder-friendly frame size the capture is pinned to (see armCapture).
let frameSize = null;
let session = null;
let canvasSource = null;
let wakeLock = null;
const endedListeners = new Set();
const interruptedListeners = new Set();
const backgroundedListeners = new Set();

const isTypeSupported = (type) => MediaRecorder.isTypeSupported(type);

/** The format recordings will use in this browser (`{ extension, label, isMp4, … }`). */
export function recordingFormat() {
  return recordingSupported ? pickRecordingFormat(isTypeSupported) : { mimeType: '', ...describeFormat('') };
}

export function isCaptureLive() {
  return Boolean(displayStream?.getVideoTracks().some((track) => track.readyState === 'live'));
}

/** The user stopped sharing (browser UI) — the capture is gone. */
export function onCaptureEnded(listener) {
  endedListeners.add(listener);
  return () => endedListeners.delete(listener);
}

/** The recorder stopped by itself (encoder error). Call `stopRecording()` to keep what was captured. */
export function onRecordingInterrupted(listener) {
  interruptedListeners.add(listener);
  return () => interruptedListeners.delete(listener);
}

/** Canvas mode records this canvas: `getCanvas()` returns the recap canvas (see recap/compositor.js). */
export function setCanvasSource(getCanvas) {
  canvasSource = getCanvas;
}

/** Canvas mode only: the page was in the background while recording, so the video froze for a while. */
export function onRecordingBackgrounded(listener) {
  backgroundedListeners.add(listener);
  return () => backgroundedListeners.delete(listener);
}

async function holdScreenAwake() {
  try {
    wakeLock = (await navigator.wakeLock?.request('screen')) ?? null;
  } catch {
    wakeLock = null; // best effort: denied on low battery or when the page is hidden
  }
}

function releaseScreenAwake() {
  wakeLock?.release().catch(() => {});
  wakeLock = null;
}

// A hidden page stops drawing (rAF is paused) and the OS drops the wake lock.
document.addEventListener('visibilitychange', () => {
  if (!session || captureMode !== 'canvas') return;
  if (document.hidden) {
    session.hiddenAt ??= performance.now();
    return;
  }
  holdScreenAwake();
  if (session.hiddenAt) {
    const hiddenMs = performance.now() - session.hiddenAt;
    session.hiddenAt = 0;
    for (const listener of backgroundedListeners) listener(hiddenMs);
  }
});

/** Prompts for screen/tab capture, or opens the canvas stream. Throws if the user cancels or it's unsupported. */
export async function armCapture() {
  if (!recordingSupported) throw new Error(recordingUnsupportedReason());
  if (isCaptureLive()) return;
  if (captureMode === 'canvas') {
    const canvas = canvasSource?.();
    if (!canvas) throw new Error('The recap canvas is not ready.');
    displayStream = canvas.captureStream(CANVAS_FPS);
    return;
  }
  displayStream = await navigator.mediaDevices.getDisplayMedia({
    video: {
      frameRate: { ideal: 30, max: 30 },
      width: { max: MAX_EDGE },
      height: { max: MAX_EDGE },
      displaySurface: 'browser',
    },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
  });
  const [track] = displayStream.getVideoTracks();
  track.contentHint = 'motion';
  track.addEventListener('ended', () => {
    displayStream = null;
    for (const listener of endedListeners) listener();
  });
  // Pin the capture to one encoder-friendly size for the whole show. Hardware H.264
  // encoders can shear every frame at widths like 1080 (seen in Brave on Windows),
  // and entering fullscreen would otherwise change the size mid-recording. Present
  // View goes fullscreen, so the screen's shape is the target; a fixed size makes
  // the browser letterbox the tab into it.
  const dpr = window.devicePixelRatio || 1;
  frameSize = encoderFrameSize(screen.width * dpr, screen.height * dpr, MAX_EDGE);
  try {
    await track.applyConstraints({
      width: { exact: frameSize.width },
      height: { exact: frameSize.height },
      frameRate: { ideal: 30, max: 30 },
    });
  } catch {
    // Not honoured here: startRecording() copies the capture onto a canvas of this size instead.
  }
}

/**
 * Copies `stream` onto a canvas of exactly `width`×`height`, letterboxed, and returns that
 * canvas's track. Used when the browser won't deliver the capture at the pinned size.
 */
function fixedSizeTrack(stream, { width, height }) {
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  video.play().catch(() => {});
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // A timer, not rAF: rAF stops in a background tab, which would freeze or empty the recording.
  const timer = setInterval(() => {
    const { videoWidth: vw, videoHeight: vh } = video;
    if (!vw || !vh) return;
    const scale = Math.min(width / vw, height / vh);
    const dw = Math.round(vw * scale);
    const dh = Math.round(vh * scale);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(video, Math.round((width - dw) / 2), Math.round((height - dh) / 2), dw, dh);
  }, 1000 / 30);
  const [track] = canvas.captureStream(30).getVideoTracks();
  return {
    track,
    stop() {
      clearInterval(timer);
      track.stop();
      video.srcObject = null;
    },
  };
}

export function releaseCapture() {
  displayStream?.getTracks().forEach((track) => track.stop());
  displayStream = null;
  frameSize = null;
}

/** Starts the session recording. `audioTrack` is optional. No-op if one is running. */
export function startRecording(audioTrack) {
  if (!isCaptureLive()) throw new Error('Screen capture is not active.');
  if (session) return;
  let [video] = displayStream.getVideoTracks();
  // The browser ignored the pinned size (or it changed since): record a fixed-size copy instead.
  let fixed = null;
  const { width, height } = video.getSettings();
  if (captureMode === 'screen' && frameSize && (width !== frameSize.width || height !== frameSize.height)) {
    fixed = fixedSizeTrack(displayStream, frameSize);
    video = fixed.track;
  }
  const tracks = [video];
  const withAudio = audioTrack?.readyState === 'live';
  if (withAudio) tracks.push(audioTrack);

  const { mimeType } = pickRecordingFormat(isTypeSupported, { audio: withAudio });
  const recorder = new MediaRecorder(new MediaStream(tracks), {
    ...(mimeType ? { mimeType } : {}),
    videoBitsPerSecond: captureMode === 'canvas' ? CANVAS_VIDEO_BITS_PER_SECOND : VIDEO_BITS_PER_SECOND,
    ...(withAudio ? { audioBitsPerSecond: AUDIO_BITS_PER_SECOND } : {}),
  });
  const current = { recorder, chunks: [], startedAt: performance.now(), fixed };
  recorder.addEventListener('dataavailable', (event) => {
    if (event.data?.size) current.chunks.push(event.data);
  });
  recorder.addEventListener('error', (event) => console.error('MediaRecorder error', event.error ?? event));
  recorder.addEventListener('stop', () => {
    current.stoppedAt = performance.now();
    if (session === current) for (const listener of interruptedListeners) listener();
  });
  // Regular slices keep the final Blob assembly cheap on long shows.
  recorder.start(1000);
  session = current;
  if (captureMode === 'canvas') holdScreenAwake();
}

/**
 * Stops the session recording.
 * Resolves with `{ blob, durationMs, mimeType, extension, label, isMp4 }`, or null.
 */
export function stopRecording() {
  if (!session) return Promise.resolve(null);
  const current = session;
  const { recorder, chunks } = current;
  session = null;
  releaseScreenAwake();
  return new Promise((resolve) => {
    const finish = () => {
      current.fixed?.stop();
      const mimeType = recorder.mimeType || chunks[0]?.type || '';
      const format = describeFormat(mimeType);
      const blob = new Blob(chunks, { type: format.container });
      const durationMs = (current.stoppedAt ?? performance.now()) - current.startedAt;
      resolve(blob.size ? { blob, durationMs, mimeType, ...format } : null);
    };
    if (recorder.state === 'inactive') return finish();
    recorder.addEventListener('stop', finish, { once: true });
    recorder.stop();
  });
}

export const isRecording = () => Boolean(session);
export const recordingElapsedMs = () => (session ? performance.now() - session.startedAt : 0);
